import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, Users, Radio, KeyRound } from "lucide-react";
import { ToolSpecs } from "@/components/ToolSpecs";

const CERCLE_IMG = "https://static.prod-images.emergentagent.com/jobs/08c324fb-59b2-4fff-a725-0bafcdd9ead9/images/acb5b87b85447d4193e4a7e4db2df85f9c79ebf8cb48beba42f2601640f87e50.jpeg";

const T = {
  fr: { badge: "En direct", title: "Le Cercle de réflexion", sub: "Une question, deux à cinq esprits, en temps réel. L'Élan tisse le lien entre vous — et un Sceau à garder à la fin.", cta: "Ouvrir un Cercle", f1: "2 à 5 esprits", f2: "en temps réel", f3: "un Sceau à garder" },
  en: { badge: "Live", title: "The Circle of Reflection", sub: "One question, two to five minds, in real time. L'Élan weaves the bond between you — and a Seal to keep at the end.", cta: "Open a Circle", f1: "2 to 5 minds", f2: "in real time", f3: "a Seal to keep" },
  es: { badge: "En directo", title: "El Círculo de Reflexión", sub: "Una pregunta, de dos a cinco mentes, en tiempo real. L'Élan teje el vínculo entre ustedes — y un Sello para guardar al final.", cta: "Abrir un Círculo", f1: "2 a 5 mentes", f2: "en tiempo real", f3: "un Sello para guardar" },
  nl: { badge: "Live", title: "De Reflectiekring", sub: "Eén vraag, twee tot vijf geesten, in realtime. L'Élan weeft de band tussen jullie — en een Zegel om te bewaren.", cta: "Open een Cirkel", f1: "2 tot 5 geesten", f2: "in realtime", f3: "een Zegel om te bewaren" },
};

export const CercleTeaser = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const lng = (i18n.language || "fr").slice(0, 2);
  const c = T[lng] || T.fr;

  const openCercle = () => {
    localStorage.setItem("coccon_pending_tab", "cercle");
    navigate("/atelier");
  };

  return (
    <section className="relative px-4 md:px-8 py-16" data-testid="cercle-teaser">
      <div className="max-w-6xl mx-auto relative overflow-hidden rounded-[2rem] border border-primary/25 glass frame-pulse">
        <div aria-hidden className="absolute -right-24 -top-24 w-[520px] h-[520px] rounded-full pointer-events-none"
          style={{ background: "radial-gradient(circle, hsl(28 96% 56% / 0.18), transparent 62%)" }} />
        <div className="relative z-10 grid lg:grid-cols-2 gap-8 items-center p-8 sm:p-12">
          <div>
            <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-semibold px-3 py-1 rounded-full bg-primary text-background shadow-[0_0_14px_hsl(38_90%_55%/0.5)]">
              <Radio size={12} /> {c.badge}
            </span>
            <h2 className="mt-5 font-display text-gradient-orange text-4xl sm:text-5xl leading-none pb-[0.05em]">{c.title}</h2>
            <p className="mt-4 text-foreground/65 leading-relaxed max-w-md">{c.sub}</p>
            <ToolSpecs lang={lng} className="mt-6 max-w-md"
              items={[{ Icon: Users, text: c.f1 }, { Icon: Radio, text: c.f2 }, { Icon: KeyRound, text: c.f3 }]} />
            <button onClick={openCercle} data-testid="cercle-teaser-cta"
              className="btn-frost-orange mt-8 inline-flex items-center gap-2 px-8 py-3.5 rounded-full font-semibold">
              {c.cta} <ArrowRight size={17} />
            </button>
          </div>
          {/* Visuel du Cercle */}
          <div className="relative flex items-center justify-center" data-testid="cercle-teaser-visual">
            <div className="relative w-full">
              <div aria-hidden className="pointer-events-none absolute -inset-6 -z-10">
                <div className="absolute inset-0 rounded-[3rem] blur-[80px] opacity-40"
                  style={{ background: "radial-gradient(ellipse at 50% 44%, hsl(42 90% 60% / 0.22), hsl(30 85% 52% / 0.10) 48%, transparent 74%)" }} />
              </div>
              <div className="relative rounded-[2rem] p-[2px] sm:p-[2.5px] shine-gold"
                style={{
                  backgroundImage: "linear-gradient(135deg, hsl(43 62% 62%) 0%, hsl(36 55% 50%) 30%, hsl(30 48% 42%) 52%, hsl(42 58% 58%) 76%, hsl(33 50% 46%) 100%)",
                  boxShadow: "0 0 46px -20px hsl(40 80% 54% / 0.3), 0 20px 56px -34px rgba(0,0,0,0.78), inset 0 0 30px -24px hsl(46 80% 74% / 0.35)",
                }}>
                <div className="rounded-[1.75rem] p-1.5 sm:p-2 bg-background/85 backdrop-blur-md">
                  <div className="rounded-[1.4rem] overflow-hidden">
                    <img src={CERCLE_IMG} alt={c.title} data-testid="cercle-teaser-img" className="w-full h-auto block" loading="lazy" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
